import { type ReactNode, useState } from 'react';

import { colour, radius, space, text } from './theme';

/**
 * The right-hand rail, and the sections that live in it.
 *
 * ## The problem this replaces
 *
 * The panels had grown to six, and each had placed itself in whichever corner was free when it
 * was written: objects top-right, style bottom-right, solid and angle in a second column beside
 * it, transform in a *third*, measurements bottom-left. Every one had its own card chrome, so
 * the screen showed six differently-shaped white boxes at four different addresses.
 *
 * Three things were wrong with that, in order of how much they cost the user:
 *
 * 1. **No single address for "what can I do with this?"** Selecting a segment could change the
 *    bottom-right, the second column and the bottom-left at once, and the user has to learn
 *    where to look for each. Every mature drawing tool — Illustrator, Figma, Fusion, Blender —
 *    answers that question in exactly one place, because it is one question.
 * 2. **The canvas was walled in.** Two right-hand columns plus a third for transform took
 *    roughly 690 px of width, permanently, whether or not anything was in them.
 * 3. **Bottom-anchored panels grow upward**, so the header a user is reading moves as the
 *    content changes. Top-anchored is calmer, and calm matters in a tool people use for hours.
 *
 * ## The arrangement
 *
 * One rail, scrolled as a whole, holding stacked sections:
 *
 * - **Objects** — the navigator. What is in the drawing, and how it is layered.
 * - The **inspector** proper, which is contextual: Transform, Solid, Angle, Measurements.
 * - **Style** last, because it is the only one that is always present. A section that never
 *   disappears makes a stable floor; if it sat at the top it would push the contextual ones up
 *   and down as the selection changed.
 *
 * A section that has nothing to say renders nothing at all, so the rail is short when the
 * drawing is simple. The alternative — greying sections out — fills the rail with things the
 * user must read and reject.
 *
 * ## Why the rail collapses rather than hides
 *
 * A hidden rail is a rail the user cannot find again. Collapsed, it leaves a thin edge they
 * can click, which is the difference between "put this away for a moment" and "lose it".
 *
 * ## Why the sections are one surface and not six
 *
 * They were six cards with six shadows, stacked with a gap between each. At rest that is six
 * borders and six drop shadows down one edge of the screen, which is a great deal of chrome to
 * say "here are some controls". They are now panes of a **single** panel divided by hairlines:
 * one border, one shadow, one object. The rail reads as part of the application rather than as
 * a pile of floating notes.
 */

export const RAIL_WIDTH = 248;

export interface InspectorProps {
  readonly children: ReactNode;
}

export function Inspector({ children }: InspectorProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div
      style={{
        position: 'absolute',
        right: space.wide,
        top: space.wide,
        bottom: space.wide,
        width: collapsed ? 30 : RAIL_WIDTH,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: space.snug,
        pointerEvents: 'none',
      }}
    >
      <button
        type="button"
        className="es-btn es-float"
        onClick={() => {
          setCollapsed((value) => !value);
        }}
        title={collapsed ? 'Mở bảng điều khiển' : 'Thu gọn bảng điều khiển'}
        aria-expanded={!collapsed}
        style={{
          alignSelf: 'flex-end',
          width: collapsed ? 30 : 'auto',
          padding: '3px 9px',
          borderRadius: radius.sm,
          pointerEvents: 'auto',
        }}
      >
        {collapsed ? '‹' : '›'}
      </button>

      {!collapsed && (
        <div
          className="es-panel es-scroll"
          style={{
            /*
             * The scroll lives here rather than on each section, so a long object list and a
             * long style panel share one scrollbar. Six independently scrolling boxes is the
             * arrangement that makes a rail feel like a filing cabinet.
             */
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: 'auto',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export interface PanelSectionProps {
  readonly title: string;
  /** A short count or state shown beside the title, e.g. how many objects are selected. */
  readonly badge?: string;
  /** Starts folded. For sections that are long and rarely the reason a user came here. */
  readonly initiallyCollapsed?: boolean;
  /**
   * Unfolds the section when this becomes true. For a section whose subject has just been
   * selected.
   *
   * ## The defect this closes
   *
   * `initiallyCollapsed` is read **once**, when the section mounts. Every other contextual
   * section returns `null` when it has nothing to say, so it simply appears when it becomes
   * relevant; `GraphPanel` cannot, because it doubles as the place a graph is *created* and so
   * has to stay mounted. It folded itself at mount instead — and then, having decided, never
   * changed its mind.
   *
   * So opening a drawing that already had a curve in it, or building one from the script panel,
   * left the section folded for the rest of the session. Selecting the curve made no difference,
   * and the `+ Bảng biến thiên` button inside it was simply unreachable: the rail said it is
   * contextual and then was not.
   *
   * Only the false-to-true edge opens it. A user who folds the section back while the curve is
   * still selected has said something, and being overruled on the next render would be worse
   * than the bug.
   */
  readonly openWhen?: boolean;
  /** Removes the body's padding, for a section whose content manages its own rows. */
  readonly flush?: boolean;
  readonly children: ReactNode;
}

/**
 * One card in the rail.
 *
 * Every section shares this chrome, which is most of what makes a rail look designed rather
 * than accumulated. The header doubles as the fold control — the same idiom as the toolbar —
 * because a separate × on a panel raises the question of how to get it back.
 */
export function PanelSection({
  title,
  badge,
  initiallyCollapsed = false,
  openWhen,
  flush = false,
  children,
}: PanelSectionProps) {
  const [collapsed, setCollapsed] = useState(initiallyCollapsed);

  // Adjusted during render rather than in an effect: React's own recommendation for state that
  // follows a prop, and it means the section is never painted folded for one frame first.
  const [seen, setSeen] = useState(openWhen);
  if (openWhen !== seen) {
    setSeen(openWhen);
    if (openWhen === true) setCollapsed(false);
  }

  return (
    <section
      style={{
        font: text.body,
        // A hairline between panes rather than a gap between cards: one object, several rooms.
        borderBottom: `1px solid ${colour.hairline}`,
        // A section must be allowed to shrink inside the rail's scroll, or a long one would
        // push the others out of reach instead of scrolling.
        flexShrink: 0,
      }}
    >
      <button
        type="button"
        onClick={() => {
          setCollapsed((value) => !value);
        }}
        aria-expanded={!collapsed}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: space.base,
          width: '100%',
          padding: '9px 11px',
          border: 'none',
          background: 'none',
          font: text.title,
          color: colour.strong,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span>
          {title}
          {badge !== undefined && (
            <span style={{ marginLeft: space.snug, fontWeight: 400, color: colour.faint }}>
              {badge}
            </span>
          )}
        </span>
        {/*
          A chevron that turns rather than a character that changes.

          Swapping the glyph for a different one redraws a mark of a different width, so the
          header twitches on every fold. Rotating one keeps it in place, and the turn reads as
          the panel opening rather than as the label being rewritten.
        */}
        <span
          aria-hidden
          style={{
            display: 'inline-block',
            color: colour.faint,
            fontSize: 9,
            lineHeight: 1,
            transform: collapsed ? 'rotate(-90deg)' : 'none',
            transition: 'transform var(--es-quick)',
          }}
        >
          ▼
        </span>
      </button>

      {!collapsed && (
        <div
          style={{
            padding: flush ? 0 : `0 ${String(space.wide)}px ${String(space.wide)}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: space.base,
          }}
        >
          {children}
        </div>
      )}
    </section>
  );
}
